import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { 
  Plus, Clock, DollarSign, Calendar as CalendarIcon, Edit2, Trash2,
  ChevronLeft, ChevronRight, Briefcase, TrendingUp, Download
} from "lucide-react";

const ShiftForm = ({ shift, setShift, onSubmit, submitText, onCancel }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="date">Date *</Label>
        <Input
          id="date"
          type="date"
          value={shift.date}
          onChange={(e) => setShift({ ...shift, date: e.target.value })}
          className="mt-2 bg-white border-[#E5DFEC]"
          required
        />
      </div>
      <div>
        <Label htmlFor="clinic">Clinic/Office</Label>
        <Input
          id="clinic"
          value={shift.clinic_name}
          onChange={(e) => setShift({ ...shift, clinic_name: e.target.value })}
          placeholder="Office name"
          className="mt-2 bg-white border-[#E5DFEC]"
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="start">Start Time *</Label>
        <Input
          id="start"
          type="time"
          value={shift.start_time}
          onChange={(e) => setShift({ ...shift, start_time: e.target.value })}
          className="mt-2 bg-white border-[#E5DFEC]"
          required
        />
      </div>
      <div>
        <Label htmlFor="end">End Time *</Label>
        <Input
          id="end"
          type="time"
          value={shift.end_time}
          onChange={(e) => setShift({ ...shift, end_time: e.target.value })}
          className="mt-2 bg-white border-[#E5DFEC]"
          required
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="break">Break (minutes)</Label>
        <Input
          id="break"
          type="number"
          inputMode="numeric"
          min="0"
          // Store the raw string so partial inputs (e.g., "" while clearing,
          // or numbers being typed) don't get coerced and reset the caret.
          value={shift.break_minutes ?? ""}
          onChange={(e) => setShift({ ...shift, break_minutes: e.target.value })}
          className="mt-2 bg-white border-[#E5DFEC]"
          data-testid="break-minutes-input"
        />
      </div>
      <div>
        <Label htmlFor="rate">Hourly Rate ($CAD)</Label>
        <Input
          id="rate"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={shift.hourly_rate ?? ""}
          onChange={(e) => setShift({ ...shift, hourly_rate: e.target.value })}
          className="mt-2 bg-white border-[#E5DFEC]"
          data-testid="hourly-rate-input"
        />
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <Checkbox
        id="is_paid"
        checked={shift.is_paid}
        onCheckedChange={(checked) => setShift({ ...shift, is_paid: checked })}
      />
      <Label htmlFor="is_paid" className="cursor-pointer">I have been paid for this shift</Label>
    </div>
    {shift.is_paid && (
      <div>
        <Label htmlFor="payment_method">Method of payment</Label>
        <Select
          value={shift.payment_method || ""}
          onValueChange={(v) => setShift({ ...shift, payment_method: v })}
        >
          <SelectTrigger id="payment_method" className="mt-2 bg-white border-[#E5DFEC]" data-testid="payment-method-input">
            <SelectValue placeholder="How were you paid?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
            <SelectItem value="cheque">Cheque</SelectItem>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="e_transfer">E-Transfer</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
    <div>
      <Label htmlFor="t4_issued">Will a T4 be issued for this shift?</Label>
      <Select
        value={shift.t4_issued || ""}
        onValueChange={(v) => setShift({ ...shift, t4_issued: v })}
      >
        <SelectTrigger id="t4_issued" className="mt-2 bg-white border-[#E5DFEC]" data-testid="t4-issued-input">
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
          <SelectItem value="unsure">Not sure</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label htmlFor="notes">Notes</Label>
      <Textarea
        id="notes"
        value={shift.notes}
        onChange={(e) => setShift({ ...shift, notes: e.target.value })}
        placeholder="Any notes about this shift..."
        className="mt-2 bg-white border-[#E5DFEC]"
      />
    </div>
    <p className="text-xs text-[#94A3B8] leading-relaxed bg-[#F0ECF7]/50 border border-[#E5DFEC] rounded-lg p-3">
      Please verify all information is correct. DHDH is not responsible for any incorrect or incomplete information entered.
    </p>
    <div className="flex justify-end gap-3 pt-2">
      <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">
        Cancel
      </Button>
      <Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full">
        {submitText}
      </Button>
    </div>
  </form>
);

const ShiftTrackerPage = () => {
  const { user } = useAuth();
  // Parse a YYYY-MM-DD string in LOCAL time (vs `new Date(str)` which parses
  // it as UTC midnight and drifts a day earlier in negative-UTC zones).
  const parseLocalDate = (s) => {
    if (!s) return new Date();
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };
  const formatShiftDate = (s) =>
    parseLocalDate(s).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [showAddShift, setShowAddShift] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState("calendar"); // calendar or list
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false);
  const [newShift, setNewShift] = useState({
    date: "",
    start_time: "09:00",
    end_time: "17:00",
    break_minutes: 30,
    hourly_rate: 45,
    is_paid: true,
    payment_method: "",
    t4_issued: "",
    notes: "",
    clinic_name: ""
  });

  useEffect(() => {
    fetchShifts();
  }, [currentMonth]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      const [shiftsRes, calendarRes] = await Promise.all([
        axios.get(`${API}/shifts?month=${year}-${String(month).padStart(2, '0')}`),
        axios.get(`${API}/shifts/calendar/${year}/${month}`)
      ]);
      
      setShifts(shiftsRes.data.shifts);
      setSummary(shiftsRes.data.summary);
      setCalendarData(calendarRes.data.calendar);
    } catch (error) {
      toast.error("Failed to load shifts");
    } finally {
      setLoading(false);
    }
  };

  const handleAddShift = async (e) => {
    e.preventDefault();
    if (!newShift.date || !newShift.start_time || !newShift.end_time) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/shifts`, {
        ...newShift,
        break_minutes: parseInt(newShift.break_minutes, 10) || 0,
        hourly_rate: parseFloat(newShift.hourly_rate) || 0,
        payment_method: newShift.is_paid ? (newShift.payment_method || null) : null,
        t4_issued: newShift.t4_issued || null,
      });
      toast.success("Shift added!");
      setShowAddShift(false);
      resetForm();
      fetchShifts();
    } catch (error) {
      toast.error("Failed to add shift");
    }
  };

  const handleUpdateShift = async (e) => {
    e.preventDefault();
    if (!editingShift) return;

    try {
      await axios.put(`${API}/shifts/${editingShift.shift_id}`, {
        date: editingShift.date,
        start_time: editingShift.start_time,
        end_time: editingShift.end_time,
        break_minutes: parseInt(editingShift.break_minutes, 10) || 0,
        hourly_rate: parseFloat(editingShift.hourly_rate) || 0,
        is_paid: editingShift.is_paid,
        payment_method: editingShift.is_paid ? (editingShift.payment_method || null) : null,
        t4_issued: editingShift.t4_issued || null,
        notes: editingShift.notes,
        clinic_name: editingShift.clinic_name
      });
      toast.success("Shift updated!");
      setEditingShift(null);
      fetchShifts();
    } catch (error) {
      toast.error("Failed to update shift");
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm("Delete this shift?")) return;

    try {
      await axios.delete(`${API}/shifts/${shiftId}`);
      toast.success("Shift deleted");
      fetchShifts();
    } catch (error) {
      toast.error("Failed to delete shift");
    }
  };

  const handleExportPdf = async () => {
    const year = currentMonth.getFullYear();
    try {
      toast.loading(`Building ${year} shift summary…`, { id: "shift-pdf" });
      const res = await axios.get(`${API}/shifts/export/pdf?year=${year}`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `DHDH_Shift_Summary_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success(`${year} summary downloaded`, { id: "shift-pdf" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF export failed", { id: "shift-pdf" });
    }
  };

  const resetForm = () => {
    setNewShift({
      date: "",
      start_time: "09:00",
      end_time: "17:00",
      break_minutes: 30,
      hourly_rate: 45,
      is_paid: true,
      payment_method: "",
      t4_issued: "",
      notes: "",
      clinic_name: ""
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty days for alignment
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({
        day: i,
        date: dateStr,
        shifts: calendarData[dateStr] || []
      });
    }
    
    return days;
  };

  const openAddShiftForDate = (date) => {
    setNewShift({ ...newShift, date });
    setShowAddShift(true);
  };


  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="shift-tracker-page">
        {/* Disclaimer */}
        <div className="bg-[#F0ECF7]/70 border border-[#E5DFEC] rounded-xl p-3 mb-6 flex items-start gap-2" data-testid="shift-disclaimer">
          <Briefcase className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B4E8A] leading-relaxed">
            All financial and tax information is provided for organizational purposes only. DHDH is not a licensed tax advisor. Always verify your information with a qualified CPA or the CRA directly.
          </p>
        </div>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <EditableText
              blockKey="shifts.title"
              fallback="Shift Tracker"
              as="h1"
              className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="shifts.subtitle"
              fallback="Track your hours, breaks, and earnings"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={showUnpaidOnly ? "default" : "outline"}
              className={`rounded-full ${showUnpaidOnly
                ? "bg-[#EF4444] text-white hover:bg-[#DC2626]"
                : "border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]"}`}
              onClick={() => setShowUnpaidOnly((v) => !v)}
              data-testid="toggle-unpaid-only-btn"
              title="Filter list to unpaid shifts only"
            >
              {showUnpaidOnly ? "Showing Unpaid Only" : "Show Unpaid Only"}
              {summary.unpaid_shifts > 0 && (
                <span className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] rounded-full ${
                  showUnpaidOnly ? "bg-white text-[#EF4444]" : "bg-[#EF4444] text-white"
                }`}>
                  {summary.unpaid_shifts}
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-[#6B4E8A] text-[#6B4E8A] hover:bg-[#F0ECF7]"
              onClick={handleExportPdf}
              data-testid="export-shift-pdf-btn"
              title="Download annual practice-hours summary as PDF"
            >
              <Download className="w-4 h-4 mr-2" />
              {currentMonth.getFullYear()} PDF
            </Button>
            <Dialog open={showAddShift} onOpenChange={setShowAddShift}>
              <DialogTrigger asChild>
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="add-shift-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Shift
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Shift</DialogTitle>
                </DialogHeader>
                <ShiftForm
                  shift={newShift}
                  setShift={setNewShift}
                  onSubmit={handleAddShift}
                  onCancel={() => setShowAddShift(false)}
                  submitText="Add Shift"
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#6B4E8A]/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#6B4E8A]" />
              </div>
              <div>
                <p className="text-2xl font-medium text-[#6B4E8A]">{summary.total_shifts || 0}</p>
                <p className="text-xs text-[#64748B]">Shifts</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#8DB5A0]/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#8DB5A0]" />
              </div>
              <div>
                <p className="text-2xl font-medium text-[#6B4E8A]">{summary.total_hours || 0}</p>
                <p className="text-xs text-[#64748B]">Hours</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#10B981]" />
              </div>
              <div>
                <p className="text-2xl font-medium text-[#6B4E8A]">{formatCurrency(summary.total_pay || 0)}</p>
                <p className="text-xs text-[#64748B]">Earned</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowUnpaidOnly((v) => !v)}
            className={`bg-white border rounded-2xl p-5 text-left transition-colors ${
              (summary.unpaid_hours || 0) > 0
                ? 'border-[#FCA5A5] hover:bg-[#FEF2F2]'
                : 'border-[#E5DFEC] hover:bg-[#FAF7F2]'
            }`}
            data-testid="unpaid-summary-card"
            title="Click to toggle unpaid-only filter"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                (summary.unpaid_hours || 0) > 0 ? 'bg-[#EF4444]/10' : 'bg-[#8DB5A0]/10'
              }`}>
                <TrendingUp className={`w-5 h-5 ${(summary.unpaid_hours || 0) > 0 ? 'text-[#EF4444]' : 'text-[#8DB5A0]'}`} />
              </div>
              <div>
                <p className={`text-2xl font-medium ${(summary.unpaid_hours || 0) > 0 ? 'text-[#EF4444]' : 'text-[#6B4E8A]'}`}>
                  {summary.unpaid_hours || 0}
                </p>
                <p className="text-xs text-[#64748B]">Unpaid Hours</p>
              </div>
            </div>
          </button>
        </motion.div>

        {/* Calendar Navigation */}
        <div className="bg-white border border-[#E5DFEC] rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <button onClick={prevMonth} className="p-2 hover:bg-[#F0ECF7] rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {currentMonth.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })}
            </h2>
            <button onClick={nextMonth} className="p-2 hover:bg-[#F0ECF7] rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center text-xs font-medium text-[#64748B] py-2">
                {day}
              </div>
            ))}
            {getDaysInMonth().map((dayData, idx) => (
              <div
                key={dayData?.date || `empty-${idx}`}
                className={`min-h-[80px] p-1 border border-[#E5DFEC] rounded-lg ${
                  dayData ? 'hover:bg-[#FAF7F2] cursor-pointer' : 'bg-[#F0ECF7]/50'
                }`}
                onClick={() => dayData && openAddShiftForDate(dayData.date)}
              >
                {dayData && (
                  <>
                    <div className="text-sm font-medium text-[#6B4E8A] mb-1">{dayData.day}</div>
                    {dayData.shifts.length > 0 && (
                      <div className="space-y-1">
                        {dayData.shifts.slice(0, 2).map((shift) => (
                          <div
                            key={shift.shift_id}
                            className={`text-xs p-1 rounded ${
                              shift.is_paid ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#EF4444]/10 text-[#EF4444] font-medium'
                            }`}
                            onClick={(e) => { e.stopPropagation(); setEditingShift(shift); }}
                          >
                            {shift.worked_hours}h • {formatCurrency(shift.total_pay)}
                          </div>
                        ))}
                        {dayData.shifts.length > 2 && (
                          <div className="text-xs text-[#64748B]">+{dayData.shifts.length - 2} more</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Shifts List */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-[#E5DFEC] rounded-2xl p-6"
        >
          <h3 className="text-lg font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Recent Shifts
          </h3>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-[#F0ECF7] rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : (() => {
            const visibleShifts = showUnpaidOnly ? shifts.filter(s => !s.is_paid) : shifts;
            return visibleShifts.length > 0 ? (
            <div className="space-y-3" data-testid="shift-list">
              {visibleShifts.slice(0, showUnpaidOnly ? 200 : 10).map((shift) => (
                <div
                  key={shift.shift_id}
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    !shift.is_paid ? 'bg-[#FEF2F2] border border-[#FCA5A5]' : 'bg-[#FAF7F2]'
                  }`}
                  data-testid={`shift-row-${shift.shift_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-12 rounded-full ${shift.is_paid ? 'bg-[#10B981]' : 'bg-[#EF4444]'}`}></div>
                    <div>
                      <p className={`font-medium ${!shift.is_paid ? 'text-[#B91C1C]' : 'text-[#6B4E8A]'}`}>
                        {formatShiftDate(shift.date)}
                      </p>
                      <p className="text-sm text-[#64748B]">
                        {shift.start_time} - {shift.end_time} • {shift.clinic_name || 'No clinic'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`font-medium ${!shift.is_paid ? 'text-[#B91C1C]' : 'text-[#6B4E8A]'}`}>{shift.worked_hours} hours</p>
                      <p className={`text-sm font-medium ${shift.is_paid ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        {shift.is_paid ? formatCurrency(shift.total_pay) : `Unpaid · ${formatCurrency(shift.total_pay)}`}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditingShift(shift)}
                        className="p-2 hover:bg-[#E5DFEC] rounded-lg"
                      >
                        <Edit2 className="w-4 h-4 text-[#64748B]" />
                      </button>
                      <button
                        onClick={() => handleDeleteShift(shift.shift_id)}
                        className="p-2 hover:bg-[#EF4444]/10 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4 text-[#EF4444]" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            ) : (
              <p className="text-center text-[#64748B] py-8">
                {showUnpaidOnly ? "No unpaid shifts this month" : "No shifts recorded this month"}
              </p>
            );
          })()}
        </motion.div>

        {/* Edit Shift Dialog */}
        <Dialog open={!!editingShift} onOpenChange={() => setEditingShift(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Shift</DialogTitle>
            </DialogHeader>
            {editingShift && (
              <ShiftForm
                shift={editingShift}
                setShift={setEditingShift}
                onSubmit={handleUpdateShift}
                onCancel={() => setEditingShift(null)}
                submitText="Update Shift"
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ShiftTrackerPage;
